import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Building2 } from "lucide-react";

/* ---------------------------------------------------------------------------
 * Split-from-Group dialog: pulls a pub out of its parent group into a fresh
 * independent client. Reverse of MoveToGroupButton. Backend endpoint:
 * `/api/admin/split-pub-from-group` (dry-run + confirm).
 * ------------------------------------------------------------------------- */
export function SplitFromGroupButton({ pub, onSplit }) {
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState(pub?.name || "");
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setPlan(null); setNewName(pub?.name || ""); };

  const preview = async () => {
    if (!newName.trim()) { toast.error("Give the new independent client a name."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/split-pub-from-group", {
        pub_slug: pub.slug,
        new_client_name: newName.trim(),
        dry_run: true,
      });
      setPlan(data.plan);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post("/admin/split-pub-from-group", {
        pub_slug: pub.slug,
        new_client_name: newName.trim(),
        dry_run: false,
      });
      toast.success(`${pub.name} split out into an independent client.`);
      setOpen(false);
      reset();
      onSplit?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Split out into an independent client"
        data-testid={`split-from-group-open-${pub.slug}`}
        className="text-xs font-semibold text-zinc-700 hover:text-zinc-950 border border-zinc-300 hover:border-zinc-950 rounded-sm px-2 h-8 inline-flex items-center gap-1"
      >
        <Building2 className="h-3.5 w-3.5" /> Split
      </button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="bg-white max-w-lg" data-testid="split-from-group-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold tracking-tight text-zinc-950">
              Split {pub.name} out of its group
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-500">
              Creates a new independent client and reassigns this pub to it. The pub keeps its slug, prize, sponsors, and every prediction. The parent group is left intact.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">New independent client name</Label>
              <Input
                value={newName}
                onChange={(e) => { setNewName(e.target.value); setPlan(null); }}
                data-testid="split-from-group-name"
                maxLength={120}
                className="mt-2 h-10 rounded-sm border-zinc-300"
              />
              <p className="text-[11px] text-zinc-500 mt-1">
                Defaults to the pub&apos;s own name. Edit if you want a different client label.
              </p>
            </div>

            {plan && (
              <div className="rounded-sm border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="split-from-group-plan">
                <p className="font-semibold mb-1">Dry run — this is what will happen:</p>
                <ul className="list-disc ml-4 space-y-1">
                  <li><span className="font-semibold">{plan.pub.name}</span> ({plan.pub.slug}) will leave <span className="font-semibold">{plan.from_client.name}</span>.</li>
                  <li>New independent client <span className="font-semibold">{plan.new_client.name}</span> will be created and this pub reassigned to it.</li>
                  <li>The group <span className="font-semibold">{plan.from_client.name}</span> stays intact (may still contain other pubs).</li>
                  <li>Predictions, users, sponsors, and prize config on the pub are untouched.</li>
                </ul>
              </div>
            )}
          </div>

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => { setOpen(false); reset(); }}
              data-testid="split-from-group-cancel"
              className="h-10 rounded-sm"
            >
              Cancel
            </Button>
            {!plan ? (
              <Button
                onClick={preview}
                disabled={!newName.trim() || busy}
                data-testid="split-from-group-preview"
                className="h-10 rounded-sm bg-zinc-950 hover:bg-black text-white"
              >
                {busy ? "Checking…" : "Preview split"}
              </Button>
            ) : (
              <Button
                onClick={confirm}
                disabled={busy}
                data-testid="split-from-group-confirm"
                className="h-10 rounded-sm bg-red-600 hover:bg-red-700 text-white"
              >
                {busy ? "Splitting…" : "Confirm split"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
